import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

declare var RTA_API: string;

// ============================================
// Router Template Interfaces
// ============================================

export interface RouterTemplate {
  id?: number;
  name: string;
  description?: string;
  productLine?: string;
  subPSL?: string;
  bitSeries?: string;
  plant?: string;
  isActive: boolean;
  isDefault: boolean;
  version: number;
  createdBy?: number;
  createdDate?: Date;
  modifiedBy?: number;
  modifiedDate?: Date;
  steps?: RouterStep[];
}

export interface RouterStep {
  id?: number;
  routerTemplateId: number;
  sequence: number;
  operationCode?: string;
  workCenter?: string;
  controlKey?: string;
  description: string;
  longText?: string;
  standardTime?: number;
  timeUnit?: string;
  isRequired: boolean;
  isParallel: boolean;
  standardTextKey?: string;
  activityType?: string;
  category?: string;
  additionalConfig?: string;
  isActive: boolean;
}

export interface FeatureRouterMapping {
  id?: number;
  bitFeatureId: number;
  bitFeatureName?: string; // For display
  bitFeatureCode?: string; // For display
  routerTemplateId?: number;
  routerTemplateName?: string; // For display
  targetStepId?: number;
  targetStepDescription?: string; // For display
  action: FeatureRouterAction;
  priority: number;
  newStepSequence?: number;
  newStepWorkCenter?: string;
  newStepDescription?: string;
  newStepStandardTime?: number;
  newStepLongText?: string;
  modificationData?: string;
  condition?: string;
  isActive: boolean;
  createdBy?: number;
  createdDate?: Date;
  modifiedBy?: number;
  modifiedDate?: Date;
}

export enum FeatureRouterAction {
  AddStep = 1,
  RemoveStep = 2,
  ModifyStep = 3,
  InsertBefore = 4,
  InsertAfter = 5,
  ReplaceStep = 6,
  SkipStep = 7,
  AppendInstructions = 8
}

export const FeatureRouterActionLabels: { [key: number]: string } = {
  [FeatureRouterAction.AddStep]: 'Add Step',
  [FeatureRouterAction.RemoveStep]: 'Remove Step',
  [FeatureRouterAction.ModifyStep]: 'Modify Step',
  [FeatureRouterAction.InsertBefore]: 'Insert Before',
  [FeatureRouterAction.InsertAfter]: 'Insert After',
  [FeatureRouterAction.ReplaceStep]: 'Replace Step',
  [FeatureRouterAction.SkipStep]: 'Skip Step',
  [FeatureRouterAction.AppendInstructions]: 'Append Instructions'
};

export interface WorkCenter {
  code: string;
  name: string;
  plant?: string;
  description?: string;
}

export interface BitFeatureOption {
  id: number;
  code: string;
  name: string;
  subPSL?: string;
  category?: string;
}

// ============================================
// Router Admin Service
// ============================================

@Injectable({
  providedIn: 'root'
})
export class RouterAdminService {
  private baseUrl: string = RTA_API.replace('/RTA', '/RouterAdmin');

  constructor(private http: HttpClient) { }

  // ============================================
  // Router Templates
  // ============================================

  getTemplates(): Observable<RouterTemplate[]> {
    return this.http.get<RouterTemplate[]>(`${this.baseUrl}/templates`);
  }

  getTemplate(id: number): Observable<RouterTemplate> {
    return this.http.get<RouterTemplate>(`${this.baseUrl}/templates/${id}`);
  }

  getTemplateWithSteps(id: number): Observable<RouterTemplate> {
    return this.http.get<RouterTemplate>(`${this.baseUrl}/templates/${id}/with-steps`);
  }

  createTemplate(template: RouterTemplate): Observable<RouterTemplate> {
    return this.http.post<RouterTemplate>(`${this.baseUrl}/templates`, template);
  }

  updateTemplate(id: number, template: RouterTemplate): Observable<RouterTemplate> {
    return this.http.put<RouterTemplate>(`${this.baseUrl}/templates/${id}`, template);
  }

  deleteTemplate(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/templates/${id}`);
  }

  duplicateTemplate(id: number, newName: string): Observable<RouterTemplate> {
    return this.http.post<RouterTemplate>(`${this.baseUrl}/templates/${id}/duplicate`, { newName });
  }

  setDefaultTemplate(id: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/templates/${id}/set-default`, {});
  }

  // ============================================
  // Router Steps
  // ============================================

  getSteps(templateId: number): Observable<RouterStep[]> {
    return this.http.get<RouterStep[]>(`${this.baseUrl}/templates/${templateId}/steps`);
  }

  getStep(templateId: number, stepId: number): Observable<RouterStep> {
    return this.http.get<RouterStep>(`${this.baseUrl}/templates/${templateId}/steps/${stepId}`);
  }

  createStep(templateId: number, step: RouterStep): Observable<RouterStep> {
    return this.http.post<RouterStep>(`${this.baseUrl}/templates/${templateId}/steps`, step);
  }

  updateStep(templateId: number, stepId: number, step: RouterStep): Observable<RouterStep> {
    return this.http.put<RouterStep>(`${this.baseUrl}/templates/${templateId}/steps/${stepId}`, step);
  }

  deleteStep(templateId: number, stepId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/templates/${templateId}/steps/${stepId}`);
  }

  reorderSteps(templateId: number, stepIds: number[]): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/templates/${templateId}/steps/reorder`, { stepIds });
  }

  // ============================================
  // Feature Router Mappings
  // ============================================

  getMappings(templateId?: number): Observable<FeatureRouterMapping[]> {
    const url = templateId
      ? `${this.baseUrl}/mappings?templateId=${templateId}`
      : `${this.baseUrl}/mappings`;
    return this.http.get<FeatureRouterMapping[]>(url);
  }

  getMappingsByFeature(featureId: number): Observable<FeatureRouterMapping[]> {
    return this.http.get<FeatureRouterMapping[]>(`${this.baseUrl}/mappings/by-feature/${featureId}`);
  }

  getMapping(id: number): Observable<FeatureRouterMapping> {
    return this.http.get<FeatureRouterMapping>(`${this.baseUrl}/mappings/${id}`);
  }

  createMapping(mapping: FeatureRouterMapping): Observable<FeatureRouterMapping> {
    return this.http.post<FeatureRouterMapping>(`${this.baseUrl}/mappings`, mapping);
  }

  updateMapping(id: number, mapping: FeatureRouterMapping): Observable<FeatureRouterMapping> {
    return this.http.put<FeatureRouterMapping>(`${this.baseUrl}/mappings/${id}`, mapping);
  }

  deleteMapping(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/mappings/${id}`);
  }

  // ============================================
  // Reference Data
  // ============================================

  getWorkCenters(plant?: string): Observable<WorkCenter[]> {
    const url = plant
      ? `${this.baseUrl}/work-centers?plant=${plant}`
      : `${this.baseUrl}/work-centers`;
    return this.http.get<WorkCenter[]>(url);
  }

  getBitFeatures(subPSL?: string): Observable<BitFeatureOption[]> {
    const url = subPSL
      ? `${this.baseUrl}/bit-features?subPSL=${subPSL}`
      : `${this.baseUrl}/bit-features`;
    return this.http.get<BitFeatureOption[]>(url);
  }

  getProductLines(): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrl}/product-lines`);
  }

  getSubPSLs(productLine?: string): Observable<string[]> {
    const url = productLine
      ? `${this.baseUrl}/sub-psls?productLine=${productLine}`
      : `${this.baseUrl}/sub-psls`;
    return this.http.get<string[]>(url);
  }

  getBitSeries(): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrl}/bit-series`);
  }

  getPlants(): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrl}/plants`);
  }

  // ============================================
  // Router Preview/Generation
  // ============================================

  previewRouter(templateId: number, featureIds: number[]): Observable<RouterStep[]> {
    return this.http.post<RouterStep[]>(`${this.baseUrl}/templates/${templateId}/preview`, { featureIds });
  }

  validateTemplate(templateId: number): Observable<{ isValid: boolean; errors: string[]; warnings: string[] }> {
    return this.http.get<{ isValid: boolean; errors: string[]; warnings: string[] }>(`${this.baseUrl}/templates/${templateId}/validate`);
  }
}
